/**
 * AxLabs Mathematics Experiments - Trigonometry
 *
 * Collection of experiments exploring trigonometric functions and relationships.
 */
import { createExperimentFromSchema } from '../../framework/index.js';
import { getParam, getState } from './helpers.js';
// ============================================================================
// UNIT CIRCLE EXPLORATION
// ============================================================================
export const UnitCircleExploration = createExperimentFromSchema({
    id: 'unit-circle-exploration',
    name: 'Unit Circle Exploration',
    domain: 'mathematics',
    description: 'Explore the unit circle and trigonometric function definitions',
    conceptTags: ['Unit Circle', 'Sine', 'Cosine', 'Radians', 'Trigonometry'],
    difficulty: 2,
    stateVariables: [
        { id: 'theta', name: 'Angle θ', unit: 'rad', initial: 0, symbol: '\\theta' },
        { id: 'cos', name: 'cos(θ)', unit: 'dimensionless', initial: 1, symbol: '\\cos\\theta' },
        { id: 'sin', name: 'sin(θ)', unit: 'dimensionless', initial: 0, symbol: '\\sin\\theta' },
        { id: 'tan', name: 'tan(θ)', unit: 'dimensionless', initial: 0, symbol: '\\tan\\theta' },
        { id: 'x', name: 'Point X', unit: 'dimensionless', initial: 1, symbol: 'x' },
        { id: 'y', name: 'Point Y', unit: 'dimensionless', initial: 0, symbol: 'y' },
        { id: 'arcLength', name: 'Arc Length', unit: 'dimensionless', initial: 0, symbol: 's' },
    ],
    parameters: [
        { id: 'angularSpeed', name: 'Angular Speed', unit: 'rad/s', min: -2, max: 2, default: 0.5, symbol: '\\omega' },
        { id: 'radius', name: 'Radius', unit: 'dimensionless', min: 0.5, max: 3, default: 1, symbol: 'r' },
    ],
    equations: [
        {
            id: 'unit-circle-def',
            name: 'Unit Circle Definition',
            latex: '\\cos\\theta = x, \\quad \\sin\\theta = y',
            description: 'On unit circle, coordinates equal cosine and sine',
        },
        {
            id: 'pythagorean',
            name: 'Pythagorean Identity',
            latex: '\\sin^2\\theta + \\cos^2\\theta = 1',
            description: 'Fundamental identity derived from unit circle',
        },
        {
            id: 'tangent-def',
            name: 'Tangent Definition',
            latex: '\\tan\\theta = \\frac{\\sin\\theta}{\\cos\\theta} = \\frac{y}{x}',
            description: 'Tangent is sine divided by cosine',
        },
    ],
    derivatives: (state, params) => {
        const angularSpeed = getParam(params, 'angularSpeed');
        const r = getParam(params, 'radius');
        const theta = getState(state, 'theta');
        const nextTheta = theta + angularSpeed * 0.016;
        const cos = r * Math.cos(nextTheta);
        const sin = r * Math.sin(nextTheta);
        const tan = Math.abs(cos) > 0.001 ? sin / cos : Math.sign(sin) * 1000;
        return {
            theta: angularSpeed * 0.016,
            cos: cos - getState(state, 'cos'),
            sin: sin - getState(state, 'sin'),
            tan: tan - getState(state, 'tan'),
            x: cos - getState(state, 'x'),
            y: sin - getState(state, 'y'),
            arcLength: r * nextTheta - getState(state, 'arcLength'),
        };
    },
    bounds: {
        theta: { min: -Math.PI * 4, max: Math.PI * 4 },
        cos: { min: -3, max: 3 },
        sin: { min: -3, max: 3 },
    },
});
// ============================================================================
// SINE WAVE PARAMETER EFFECTS
// ============================================================================
export const SineWaveParameterEffects = createExperimentFromSchema({
    id: 'sine-wave-parameter-effects',
    name: 'Sine Wave Parameter Effects',
    domain: 'mathematics',
    description: 'Explore how parameters affect sinusoidal waveforms',
    conceptTags: ['Sine Wave', 'Amplitude', 'Period', 'Phase Shift', 'Frequency'],
    difficulty: 3,
    stateVariables: [
        { id: 'x', name: 'Input x', unit: 'dimensionless', initial: 0, symbol: 'x' },
        { id: 'y', name: 'f(x)', unit: 'dimensionless', initial: 0, symbol: 'f(x)' },
        { id: 'amplitude', name: 'Amplitude |A|', unit: 'dimensionless', initial: 2, symbol: '|A|' },
        { id: 'period', name: 'Period T', unit: 'dimensionless', initial: 4, symbol: 'T' },
        { id: 'phaseShift', name: 'Phase Shift φ/B', unit: 'dimensionless', initial: 0, symbol: '\\phi/B' },
    ],
    parameters: [
        { id: 'A', name: 'Amplitude A', unit: 'dimensionless', min: -5, max: 5, default: 2, symbol: 'A' },
        { id: 'B', name: 'Frequency B', unit: 'dimensionless', min: 0.1, max: 5, default: 1, symbol: 'B' },
        { id: 'C', name: 'Phase Shift C', unit: 'rad', min: -Math.PI, max: Math.PI, default: 0, symbol: 'C' },
        { id: 'D', name: 'Vertical Shift D', unit: 'dimensionless', min: -3, max: 3, default: 0, symbol: 'D' },
        { id: 'scanRate', name: 'Scan Rate', unit: 'dimensionless/s', min: 0.01, max: 0.2, default: 0.05, symbol: 'v' },
    ],
    equations: [
        {
            id: 'sinusoidal-form',
            name: 'General Sinusoidal Form',
            latex: 'f(x) = A\\sin(Bx - C) + D',
            description: 'General form of sinusoidal function',
        },
        {
            id: 'amplitude-formula',
            name: 'Amplitude',
            latex: '|A| = \\text{amplitude}',
            description: 'Half the distance between max and min',
        },
        {
            id: 'period-formula',
            name: 'Period',
            latex: 'T = \\frac{2\\pi}{|B|}',
            description: 'Distance for one complete cycle',
        },
        {
            id: 'phase-shift-formula',
            name: 'Phase Shift',
            latex: '\\phi = \\frac{C}{B}',
            description: 'Horizontal shift of the wave',
        },
    ],
    derivatives: (state, params) => {
        const A = getParam(params, 'A');
        const B = getParam(params, 'B');
        const C = getParam(params, 'C');
        const D = getParam(params, 'D');
        const scanRate = getParam(params, 'scanRate');
        const x = getState(state, 'x');
        const nextX = x + scanRate * 0.016;
        const y = A * Math.sin(B * nextX - C) + D;
        const amplitude = Math.abs(A);
        const period = Math.abs(B) > 0.001 ? (2 * Math.PI) / Math.abs(B) : 0;
        const phaseShift = Math.abs(B) > 0.001 ? C / B : 0;
        return {
            x: scanRate * 0.016,
            y: y - getState(state, 'y'),
            amplitude: amplitude - getState(state, 'amplitude'),
            period: period - getState(state, 'period'),
            phaseShift: phaseShift - getState(state, 'phaseShift'),
        };
    },
    bounds: {
        x: { min: -10, max: 30 },
        y: { min: -10, max: 10 },
    },
});
// ============================================================================
// TRIGONOMETRIC IDENTITY VERIFICATION
// ============================================================================
export const TrigonometricIdentityVerification = createExperimentFromSchema({
    id: 'trigonometric-identity-verification',
    name: 'Trigonometric Identity Verification',
    domain: 'mathematics',
    description: 'Verify fundamental trigonometric identities',
    conceptTags: ['Trig Identities', 'Pythagorean', 'Angle Sum', 'Double Angle', 'Verification'],
    difficulty: 4,
    stateVariables: [
        { id: 'theta', name: 'Angle θ', unit: 'rad', initial: 0, symbol: '\\theta' },
        { id: 'sin', name: 'sin(θ)', unit: 'dimensionless', initial: 0, symbol: '\\sin\\theta' },
        { id: 'cos', name: 'cos(θ)', unit: 'dimensionless', initial: 1, symbol: '\\cos\\theta' },
        { id: 'lhs', name: 'LHS Value', unit: 'dimensionless', initial: 1, symbol: 'LHS' },
        { id: 'rhs', name: 'RHS Value', unit: 'dimensionless', initial: 1, symbol: 'RHS' },
        { id: 'difference', name: '|LHS - RHS|', unit: 'dimensionless', initial: 0, symbol: '|LHS-RHS|' },
        { id: 'verified', name: 'Identity Verified', unit: 'dimensionless', initial: 1, symbol: 'V' },
    ],
    parameters: [
        { id: 'identityType', name: 'Identity Type', unit: 'dimensionless', min: 0, max: 6, default: 0, symbol: 'I' },
        { id: 'scanRate', name: 'Angle Rate', unit: 'rad/s', min: 0.1, max: 2, default: 0.5, symbol: 'v' },
    ],
    equations: [
        {
            id: 'pythagorean-1',
            name: 'Pythagorean Identity 1',
            latex: '\\sin^2\\theta + \\cos^2\\theta = 1',
        },
        {
            id: 'pythagorean-2',
            name: 'Pythagorean Identity 2',
            latex: '1 + \\tan^2\\theta = \\sec^2\\theta',
        },
        {
            id: 'pythagorean-3',
            name: 'Pythagorean Identity 3',
            latex: '1 + \\cot^2\\theta = \\csc^2\\theta',
        },
        {
            id: 'double-angle-sin',
            name: 'Double Angle Sine',
            latex: '\\sin(2\\theta) = 2\\sin\\theta\\cos\\theta',
        },
        {
            id: 'double-angle-cos',
            name: 'Double Angle Cosine',
            latex: '\\cos(2\\theta) = \\cos^2\\theta - \\sin^2\\theta',
        },
        {
            id: 'angle-sum-sin',
            name: 'Angle Sum Sine',
            latex: '\\sin(\\alpha+\\beta) = \\sin\\alpha\\cos\\beta + \\cos\\alpha\\sin\\beta',
        },
        {
            id: 'angle-sum-cos',
            name: 'Angle Sum Cosine',
            latex: '\\cos(\\alpha+\\beta) = \\cos\\alpha\\cos\\beta - \\sin\\alpha\\sin\\beta',
        },
    ],
    derivatives: (state, params) => {
        const identityType = Math.round(getParam(params, 'identityType'));
        const scanRate = getParam(params, 'scanRate');
        const theta = getState(state, 'theta');
        const nextTheta = theta + scanRate * 0.016;
        const sin = Math.sin(nextTheta);
        const cos = Math.cos(nextTheta);
        const tan = Math.abs(cos) > 0.001 ? sin / cos : 0;
        const cot = Math.abs(sin) > 0.001 ? cos / sin : 0;
        const sec = Math.abs(cos) > 0.001 ? 1 / cos : 1000;
        const csc = Math.abs(sin) > 0.001 ? 1 / sin : 1000;
        let lhs = 0;
        let rhs = 0;
        switch (identityType) {
            case 0: // sin² + cos² = 1
                lhs = sin * sin + cos * cos;
                rhs = 1;
                break;
            case 1: // 1 + tan² = sec²
                lhs = 1 + tan * tan;
                rhs = sec * sec;
                break;
            case 2: // 1 + cot² = csc²
                lhs = 1 + cot * cot;
                rhs = csc * csc;
                break;
            case 3: // sin(2θ) = 2sinθcosθ
                lhs = Math.sin(2 * nextTheta);
                rhs = 2 * sin * cos;
                break;
            case 4: // cos(2θ) = cos² - sin²
                lhs = Math.cos(2 * nextTheta);
                rhs = cos * cos - sin * sin;
                break;
            case 5: // sin(α+β) with α=β=θ
                lhs = Math.sin(2 * nextTheta);
                rhs = sin * cos + cos * sin;
                break;
            case 6: // cos(α+β) with α=β=θ
                lhs = Math.cos(2 * nextTheta);
                rhs = cos * cos - sin * sin;
                break;
        }
        const difference = Math.abs(lhs - rhs);
        const verified = difference < 0.001 ? 1 : 0;
        return {
            theta: scanRate * 0.016,
            sin: sin - getState(state, 'sin'),
            cos: cos - getState(state, 'cos'),
            lhs: lhs - getState(state, 'lhs'),
            rhs: rhs - getState(state, 'rhs'),
            difference: difference - getState(state, 'difference'),
            verified: verified - getState(state, 'verified'),
        };
    },
    bounds: {
        theta: { min: 0, max: Math.PI * 4 },
        lhs: { min: -5, max: 5 },
        rhs: { min: -5, max: 5 },
    },
});
// ============================================================================
// PHASE SHIFT ANALYSIS
// ============================================================================
export const PhaseShiftAnalysis = createExperimentFromSchema({
    id: 'phase-shift-analysis',
    name: 'Phase Shift Analysis',
    domain: 'mathematics',
    description: 'Analyze phase relationships between sinusoidal functions',
    conceptTags: ['Phase Shift', 'Sinusoidal', 'Leading/Lagging', 'Phase Difference'],
    difficulty: 4,
    stateVariables: [
        { id: 'x', name: 'Input x', unit: 'dimensionless', initial: 0, symbol: 'x' },
        { id: 'y1', name: 'f₁(x)', unit: 'dimensionless', initial: 0, symbol: 'f_1(x)' },
        { id: 'y2', name: 'f₂(x)', unit: 'dimensionless', initial: 0, symbol: 'f_2(x)' },
        { id: 'phaseDiff', name: 'Phase Difference', unit: 'rad', initial: 0, symbol: '\\Delta\\phi' },
        { id: 'crossingX', name: 'Crossover X', unit: 'dimensionless', initial: 0, symbol: 'x_c' },
        { id: 'isInPhase', name: 'In Phase', unit: 'dimensionless', initial: 1, symbol: 'P' },
        { id: 'isOpposite', name: 'Out of Phase', unit: 'dimensionless', initial: 0, symbol: 'O' },
    ],
    parameters: [
        { id: 'A1', name: 'Amplitude 1', unit: 'dimensionless', min: 0.5, max: 3, default: 1, symbol: 'A_1' },
        { id: 'A2', name: 'Amplitude 2', unit: 'dimensionless', min: 0.5, max: 3, default: 1, symbol: 'A_2' },
        { id: 'phi1', name: 'Phase 1', unit: 'rad', min: -Math.PI, max: Math.PI, default: 0, symbol: '\\phi_1' },
        { id: 'phi2', name: 'Phase 2', unit: 'rad', min: -Math.PI, max: Math.PI, default: 0, symbol: '\\phi_2' },
        { id: 'scanRate', name: 'Scan Rate', unit: 'dimensionless/s', min: 0.01, max: 0.2, default: 0.05, symbol: 'v' },
    ],
    equations: [
        {
            id: 'phase-diff',
            name: 'Phase Difference',
            latex: '\\Delta\\phi = \\phi_2 - \\phi_1',
            description: 'Difference in phase angles',
        },
        {
            id: 'in-phase',
            name: 'In Phase',
            latex: '\\Delta\\phi = 2n\\pi \\implies \\text{in phase}',
            description: 'Waves align when phase difference is multiple of 2π',
        },
        {
            id: 'out-phase',
            name: 'Out of Phase',
            latex: '\\Delta\\phi = (2n+1)\\pi \\implies \\text{180° out of phase}',
            description: 'Waves are opposite when phase difference is odd multiple of π',
        },
    ],
    derivatives: (state, params) => {
        const A1 = getParam(params, 'A1');
        const A2 = getParam(params, 'A2');
        const phi1 = getParam(params, 'phi1');
        const phi2 = getParam(params, 'phi2');
        const scanRate = getParam(params, 'scanRate');
        const x = getState(state, 'x');
        const nextX = x + scanRate * 0.016;
        const y1 = A1 * Math.sin(nextX + phi1);
        const y2 = A2 * Math.sin(nextX + phi2);
        const phaseDiff = phi2 - phi1;
        // Find where functions intersect (approximate)
        const crossingX = Math.abs(A1 - A2) < 0.001 ?
            (Math.PI - phi1 - phi2) / 2 : 0;
        const isInPhase = Math.abs(phaseDiff % (2 * Math.PI)) < 0.1 ? 1 : 0;
        const isOpposite = Math.abs((phaseDiff - Math.PI) % (2 * Math.PI)) < 0.1 ? 1 : 0;
        return {
            x: scanRate * 0.016,
            y1: y1 - getState(state, 'y1'),
            y2: y2 - getState(state, 'y2'),
            phaseDiff: phaseDiff - getState(state, 'phaseDiff'),
            crossingX: crossingX - getState(state, 'crossingX'),
            isInPhase: isInPhase - getState(state, 'isInPhase'),
            isOpposite: isOpposite - getState(state, 'isOpposite'),
        };
    },
    bounds: {
        x: { min: 0, max: 20 },
        y1: { min: -5, max: 5 },
        y2: { min: -5, max: 5 },
    },
});
// ============================================================================
// AMPLITUDE AND PERIOD RELATIONSHIP
// ============================================================================
export const AmplitudeAndPeriodRelationship = createExperimentFromSchema({
    id: 'amplitude-and-period-relationship',
    name: 'Amplitude and Period Relationship',
    domain: 'mathematics',
    description: 'Explore independent and dependent properties of sinusoids',
    conceptTags: ['Amplitude', 'Period', 'Independent Properties', 'Frequency', 'Angular Frequency'],
    difficulty: 3,
    stateVariables: [
        { id: 'x', name: 'Input x', unit: 'dimensionless', initial: 0, symbol: 'x' },
        { id: 'y', name: 'f(x)', unit: 'dimensionless', initial: 0, symbol: 'f(x)' },
        { id: 'maxVal', name: 'Maximum', unit: 'dimensionless', initial: 2, symbol: 'y_{max}' },
        { id: 'minVal', name: 'Minimum', unit: 'dimensionless', initial: -2, symbol: 'y_{min}' },
        { id: 'midline', name: 'Midline', unit: 'dimensionless', initial: 0, symbol: 'y_{mid}' },
        { id: 'measuredPeriod', name: 'Measured Period', unit: 'dimensionless', initial: 6.28, symbol: 'T_{meas}' },
    ],
    parameters: [
        { id: 'amplitude', name: 'Amplitude', unit: 'dimensionless', min: 0.5, max: 5, default: 2, symbol: 'A' },
        { id: 'period', name: 'Period Target', unit: 'dimensionless', min: 2, max: 15, default: 6.28, symbol: 'T' },
        { id: 'phase', name: 'Phase Shift', unit: 'rad', min: -Math.PI, max: Math.PI, default: 0, symbol: '\\phi' },
        { id: 'vertical', name: 'Vertical Shift', unit: 'dimensionless', min: -3, max: 3, default: 0, symbol: 'D' },
        { id: 'scanRate', name: 'Scan Rate', unit: 'dimensionless/s', min: 0.01, max: 0.2, default: 0.05, symbol: 'v' },
    ],
    equations: [
        {
            id: 'independent-params',
            name: 'Independent Parameters',
            latex: 'A, T = \\frac{2\\pi}{B}, \\phi, D \\text{ are all independent}',
            description: 'Amplitude and period are independent parameters',
        },
        {
            id: 'max-min',
            name: 'Max and Min Values',
            latex: 'y_{max} = D + A, \\quad y_{min} = D - A',
            description: 'Maximum and minimum depend on amplitude and vertical shift',
        },
    ],
    derivatives: (state, params) => {
        const A = getParam(params, 'amplitude');
        const T = getParam(params, 'period');
        const phase = getParam(params, 'phase');
        const D = getParam(params, 'vertical');
        const scanRate = getParam(params, 'scanRate');
        const x = getState(state, 'x');
        const nextX = x + scanRate * 0.016;
        const B = (2 * Math.PI) / Math.max(0.1, T);
        const y = A * Math.sin(B * nextX - phase) + D;
        const maxVal = D + A;
        const minVal = D - A;
        const midline = D;
        return {
            x: scanRate * 0.016,
            y: y - getState(state, 'y'),
            maxVal: maxVal - getState(state, 'maxVal'),
            minVal: minVal - getState(state, 'minVal'),
            midline: midline - getState(state, 'midline'),
            measuredPeriod: T - getState(state, 'measuredPeriod'),
        };
    },
    bounds: {
        x: { min: 0, max: 50 },
        y: { min: -10, max: 10 },
    },
});
// ============================================================================
// INVERSE TRIG DOMAIN BEHAVIOR
// ============================================================================
export const InverseTrigDomainBehavior = createExperimentFromSchema({
    id: 'inverse-trig-domain-behavior',
    name: 'Inverse Trig Domain Behavior',
    domain: 'mathematics',
    description: 'Explore domain and range restrictions of inverse trigonometric functions',
    conceptTags: ['Inverse Functions', 'Domain Restrictions', 'arcsin', 'arccos', 'arctan'],
    difficulty: 4,
    stateVariables: [
        { id: 'x', name: 'Input x', unit: 'dimensionless', initial: -1, symbol: 'x' },
        { id: 'arcsin', name: 'arcsin(x)', unit: 'rad', initial: -1.57, symbol: '\\arcsin(x)' },
        { id: 'arccos', name: 'arccos(x)', unit: 'rad', initial: 3.14, symbol: '\\arccos(x)' },
        { id: 'arctan', name: 'arctan(x)', unit: 'rad', initial: -0.79, symbol: '\\arctan(x)' },
        { id: 'inDomain', name: 'In Domain', unit: 'dimensionless', initial: 1, symbol: 'D' },
        { id: 'sum', name: 'arcsin + arccos', unit: 'rad', initial: 1.57, symbol: '\\arcsin + \\arccos' },
    ],
    parameters: [
        { id: 'xRange', name: 'X Range', unit: 'dimensionless', min: 1, max: 5, default: 2, symbol: 'R' },
        { id: 'scanRate', name: 'Scan Rate', unit: 'dimensionless/s', min: 0.01, max: 0.1, default: 0.02, symbol: 'v' },
    ],
    equations: [
        {
            id: 'arcsin-domain',
            name: 'arcsin Domain and Range',
            latex: 'D: [-1, 1], \\quad R: [-\\frac{\\pi}{2}, \\frac{\\pi}{2}]',
            description: 'arcsin is defined for |x| ≤ 1',
        },
        {
            id: 'arccos-domain',
            name: 'arccos Domain and Range',
            latex: 'D: [-1, 1], \\quad R: [0, \\pi]',
            description: 'arccos is defined for |x| ≤ 1',
        },
        {
            id: 'arctan-domain',
            name: 'arctan Domain and Range',
            latex: 'D: (-\\infty, \\infty), \\quad R: (-\\frac{\\pi}{2}, \\frac{\\pi}{2})',
            description: 'arctan is defined for all real numbers',
        },
        {
            id: 'inverse-sum',
            name: 'Inverse Identity',
            latex: '\\arcsin(x) + \\arccos(x) = \\frac{\\pi}{2}',
            description: 'For x in [-1, 1]',
        },
    ],
    derivatives: (state, params) => {
        const xRange = getParam(params, 'xRange');
        const scanRate = getParam(params, 'scanRate');
        const x = getState(state, 'x');
        const nextX = x + scanRate * 0.016;
        const wrappedX = ((nextX + xRange) % (2 * xRange)) - xRange;
        const inDomain = Math.abs(wrappedX) <= 1 ? 1 : 0;
        const clampedX = Math.max(-1, Math.min(1, wrappedX));
        const arcsin = Math.asin(clampedX);
        const arccos = Math.acos(clampedX);
        const arctan = Math.atan(wrappedX);
        const sum = arcsin + arccos;
        return {
            x: scanRate * 0.016,
            arcsin: arcsin - getState(state, 'arcsin'),
            arccos: arccos - getState(state, 'arccos'),
            arctan: arctan - getState(state, 'arctan'),
            inDomain: inDomain - getState(state, 'inDomain'),
            sum: sum - getState(state, 'sum'),
        };
    },
    bounds: {
        x: { min: -5, max: 5 },
        arcsin: { min: -Math.PI / 2, max: Math.PI / 2 },
        arccos: { min: 0, max: Math.PI },
    },
});
// ============================================================================
// LAW OF SINES/COSINES APPLICATION
// ============================================================================
export const LawOfSinesCosinesApplication = createExperimentFromSchema({
    id: 'law-of-sines-cosines-application',
    name: 'Law of Sines/Cosines Application',
    domain: 'mathematics',
    description: 'Apply Law of Sines and Cosines to solve triangles',
    conceptTags: ['Law of Sines', 'Law of Cosines', 'Triangle Solving', 'ASA', 'SSS', 'SAS'],
    difficulty: 5,
    stateVariables: [
        { id: 'sideA', name: 'Side a', unit: 'dimensionless', initial: 5, symbol: 'a' },
        { id: 'sideB', name: 'Side b', unit: 'dimensionless', initial: 7, symbol: 'b' },
        { id: 'sideC', name: 'Side c', unit: 'dimensionless', initial: 8, symbol: 'c' },
        { id: 'angleA', name: 'Angle A', unit: 'rad', initial: 0.63, symbol: 'A' },
        { id: 'angleB', name: 'Angle B', unit: 'rad', initial: 0.91, symbol: 'B' },
        { id: 'angleC', name: 'Angle C', unit: 'rad', initial: 1.60, symbol: 'C' },
        { id: 'sumAngles', name: 'Sum of Angles', unit: 'rad', initial: 3.14, symbol: 'A+B+C' },
    ],
    parameters: [
        { id: 'givenType', name: 'Given Type', unit: 'dimensionless', min: 0, max: 3, default: 0, symbol: 'G' },
        { id: 'value1', name: 'Value 1', unit: 'dimensionless', min: 1, max: 15, default: 5, symbol: 'v_1' },
        { id: 'value2', name: 'Value 2', unit: 'dimensionless', min: 1, max: 15, default: 7, symbol: 'v_2' },
        { id: 'value3', name: 'Value 3', unit: 'dimensionless', min: 0.1, max: 15, default: 8, symbol: 'v_3' },
    ],
    equations: [
        {
            id: 'law-sines',
            name: 'Law of Sines',
            latex: '\\frac{a}{\\sin A} = \\frac{b}{\\sin B} = \\frac{c}{\\sin C}',
            description: 'Ratio of side length to sine of opposite angle is constant',
        },
        {
            id: 'law-cosines',
            name: 'Law of Cosines',
            latex: 'c^2 = a^2 + b^2 - 2ab\\cos C',
            description: 'Relates three sides and one angle',
        },
        {
            id: 'angle-sum',
            name: 'Angle Sum',
            latex: 'A + B + C = \\pi',
            description: 'Sum of angles in triangle equals 180°',
        },
    ],
    derivatives: (_state, params) => {
        const givenType = Math.round(getParam(params, 'givenType'));
        const v1 = getParam(params, 'value1');
        const v2 = getParam(params, 'value2');
        const v3 = getParam(params, 'value3');
        let sideA = 5, sideB = 7, sideC = 8;
        let angleA = 0, angleB = 0, angleC = 0;
        switch (givenType) {
            case 0: // SSS
                sideA = v1;
                sideB = v2;
                sideC = v3;
                angleC = Math.acos((sideA * sideA + sideB * sideB - sideC * sideC) / (2 * sideA * sideB));
                angleA = Math.asin(sideA * Math.sin(angleC) / sideC);
                angleB = Math.PI - angleA - angleC;
                break;
            case 1: // SAS
                sideA = v1;
                sideB = v2;
                angleC = v3 * Math.PI / 180;
                sideC = Math.sqrt(sideA * sideA + sideB * sideB - 2 * sideA * sideB * Math.cos(angleC));
                angleA = Math.asin(sideA * Math.sin(angleC) / sideC);
                angleB = Math.PI - angleA - angleC;
                break;
            case 2: // ASA
                angleA = v1 * Math.PI / 180;
                angleB = v2 * Math.PI / 180;
                angleC = Math.PI - angleA - angleB;
                sideC = v3;
                sideA = sideC * Math.sin(angleA) / Math.sin(angleC);
                sideB = sideC * Math.sin(angleB) / Math.sin(angleC);
                break;
            case 3: // AAS
                angleA = v1 * Math.PI / 180;
                angleB = v2 * Math.PI / 180;
                angleC = Math.PI - angleA - angleB;
                sideA = v3;
                sideB = sideA * Math.sin(angleB) / Math.sin(angleA);
                sideC = sideA * Math.sin(angleC) / Math.sin(angleA);
                break;
        }
        const sumAngles = angleA + angleB + angleC;
        return {
            sideA: sideA - getState(_state, 'sideA'),
            sideB: sideB - getState(_state, 'sideB'),
            sideC: sideC - getState(_state, 'sideC'),
            angleA: angleA - getState(_state, 'angleA'),
            angleB: angleB - getState(_state, 'angleB'),
            angleC: angleC - getState(_state, 'angleC'),
            sumAngles: sumAngles - getState(_state, 'sumAngles'),
        };
    },
    bounds: {
        sideA: { min: 1, max: 20 },
        sideB: { min: 1, max: 20 },
        sideC: { min: 1, max: 20 },
        angleA: { min: 0, max: Math.PI },
    },
});
// ============================================================================
// POLAR COORDINATE MAPPING
// ============================================================================
export const PolarCoordinateMapping = createExperimentFromSchema({
    id: 'polar-coordinate-mapping',
    name: 'Polar Coordinate Mapping',
    domain: 'mathematics',
    description: 'Explore polar coordinates and conversion to/from Cartesian',
    conceptTags: ['Polar Coordinates', 'Cartesian Coordinates', 'Polar Graphs', 'Rose Curves'],
    difficulty: 4,
    stateVariables: [
        { id: 'theta', name: 'Angle θ', unit: 'rad', initial: 0, symbol: '\\theta' },
        { id: 'r', name: 'Radius r', unit: 'dimensionless', initial: 2, symbol: 'r(\\theta)' },
        { id: 'x', name: 'Cartesian X', unit: 'dimensionless', initial: 2, symbol: 'x' },
        { id: 'y', name: 'Cartesian Y', unit: 'dimensionless', initial: 0, symbol: 'y' },
        { id: 'petalCount', name: 'Petal Count', unit: 'dimensionless', initial: 4, symbol: 'n' },
    ],
    parameters: [
        { id: 'curveType', name: 'Curve Type', unit: 'dimensionless', min: 0, max: 3, default: 0, symbol: 'C' },
        { id: 'a', name: 'Parameter a', unit: 'dimensionless', min: 1, max: 5, default: 2, symbol: 'a' },
        { id: 'n', name: 'Parameter n', unit: 'dimensionless', min: 1, max: 8, default: 4, symbol: 'n' },
        { id: 'angularSpeed', name: 'Angular Speed', unit: 'rad/s', min: 0.1, max: 2, default: 0.5, symbol: '\\omega' },
    ],
    equations: [
        {
            id: 'polar-cartesian',
            name: 'Polar to Cartesian Conversion',
            latex: 'x = r\\cos\\theta, \\quad y = r\\sin\\theta',
            description: 'Convert polar coordinates to Cartesian',
        },
        {
            id: 'rose-curve',
            name: 'Rose Curve',
            latex: 'r = a\\cos(n\\theta)',
            description: 'Creates rose-shaped curves',
        },
        {
            id: 'lemniscate',
            name: 'Lemniscate',
            latex: 'r^2 = a^2\\cos(2\\theta)',
            description: 'Figure-eight curve',
        },
    ],
    derivatives: (state, params) => {
        const curveType = Math.round(getParam(params, 'curveType'));
        const a = getParam(params, 'a');
        const n = Math.round(getParam(params, 'n'));
        const angularSpeed = getParam(params, 'angularSpeed');
        const theta = getState(state, 'theta');
        const nextTheta = theta + angularSpeed * 0.016;
        let r = 0;
        let petalCount = n;
        switch (curveType) {
            case 0: // Rose curve: r = a*cos(n*θ)
                r = a * Math.cos(n * nextTheta);
                petalCount = n % 2 === 0 ? 2 * n : n;
                break;
            case 1: // Rose curve sine: r = a*sin(n*θ)
                r = a * Math.sin(n * nextTheta);
                petalCount = n % 2 === 0 ? 2 * n : n;
                break;
            case 2: // Circle: r = a
                r = a;
                petalCount = 0;
                break;
            case 3: // Spiral: r = a*θ
                r = a * Math.max(0, nextTheta % (2 * Math.PI));
                petalCount = 0;
                break;
        }
        const x = r * Math.cos(nextTheta);
        const y = r * Math.sin(nextTheta);
        return {
            theta: angularSpeed * 0.016,
            r: r - getState(state, 'r'),
            x: x - getState(state, 'x'),
            y: y - getState(state, 'y'),
            petalCount: petalCount - getState(state, 'petalCount'),
        };
    },
    bounds: {
        r: { min: -5, max: 5 },
        x: { min: -10, max: 10 },
        y: { min: -10, max: 10 },
    },
});
//# sourceMappingURL=trigonometry.js.map